import { cn } from '@/lib/utils';
import hljs from 'highlight.js';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { pendingTerminalCommandState } from '@chainlit/react-client';
import { Terminal, Check } from 'lucide-react';
import { accountExpiredState } from '@/state/project';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { Card, CardContent, CardHeader } from '@/components/ui/card';

import CopyButton from './CopyButton';

interface CodeSnippetProps {
  language: string;
  children: string;
}

const HighlightedCode = ({ language, children }: CodeSnippetProps) => {
  const codeRef = useRef<HTMLElement>(null);

  if (!hljs.getLanguage(language)) {
    language = 'txt';
  }

  useEffect(() => {
    if (codeRef.current) {
      const highlighted =
        codeRef.current.getAttribute('data-highlighted') === 'yes';
      if (!highlighted) {
        hljs.highlightElement(codeRef.current);
      }
    }
  }, []);

  return (
    <pre className="m-0 min-w-0 p-3 bg-muted/40 rounded-b-md">
      <code
        ref={codeRef}
        className={`language-${language} font-mono text-sm block break-words whitespace-pre-wrap !bg-transparent !p-0`}
      >
        {children}
      </code>
    </pre>
  );
};

interface CodeProps {
  children: React.ReactNode;
  node?: {
    children?: Array<{
      properties?: {
        className?: string[];
      };
      children?: Array<{
        value?: string;
      }>;
    }>;
  };
}

export default function CodeSnippet({ ...props }: CodeProps) {
  const codeChildren = props.node?.children?.[0];
  const className = codeChildren?.properties?.className?.[0];
  const match = /language-(\w+)/.exec(className || '');
  const code = codeChildren?.children?.[0]?.value;
  
  const setPendingCommand = useSetRecoilState(pendingTerminalCommandState);
  const accountExpired = useRecoilValue(accountExpiredState);
  const [sent, setSent] = useState(false);

  const handleSendToTerminal = () => {
    if (!code || accountExpired) return;

    setPendingCommand({
      command: code.trim(),
      timestamp: Date.now()
    });

    setSent(true);
    setTimeout(() => setSent(false), 2000);
  };

  const showSyntaxHighlighter = match && code;

  const highlightedCode = showSyntaxHighlighter ? (
    <HighlightedCode language={match[1]}>{code}</HighlightedCode>
  ) : null;

  const nonHighlightedCode = showSyntaxHighlighter ? null : (
    <div
      className={cn(
        'rounded-b-md overflow-x-auto bg-muted/40 text-foreground min-w-0',
        code && 'p-3'
      )}
    >
      <code className="whitespace-pre-wrap break-words font-mono text-sm">
        {code}
      </code>
    </div>
  );

  return (
    <Card className="relative my-2 min-w-0 overflow-hidden border-term-green/25 bg-card shadow-lg shadow-black/10">
      <CardHeader className="flex flex-row items-center justify-between py-1 px-3 bg-muted border-b border-term-green/20">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-term-red" />
            <span className="w-2.5 h-2.5 rounded-full bg-term-yellow" />
            <span className="w-2.5 h-2.5 rounded-full bg-term-green" />
          </div>
          <span className="text-xs font-mono text-term-cyan uppercase tracking-wider truncate">
            {match?.[1] || 'raw'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleSendToTerminal}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-term-green hover:bg-term-green/10"
                  aria-label="Send to terminal"
                  disabled={!!accountExpired}
                >
                  {sent ? (
                    <Check className="h-4 w-4 text-term-green" />
                  ) : (
                    <Terminal className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{sent ? 'Sent to terminal' : 'Send to terminal'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <CopyButton content={code} />
        </div>
      </CardHeader>
      <CardContent className="p-0 min-w-0 bg-card">
        {highlightedCode}
        {nonHighlightedCode}
      </CardContent>
    </Card>
  );
}
